import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Phone, Users, FileText, Calendar, ChevronDown, Loader2, ExternalLink, Plus, AudioLines, Pencil, Save, X as XIcon, Trash2 } from 'lucide-react';
import { AddMeetingDialog } from './AddMeetingDialog';
import { NotesMarkdown } from './NotesMarkdown';
import { CollapsibleSection } from './CollapsibleSection';

const EVENT_ICONS = { meeting: Users, note: FileText, call: Phone, team_meeting: Users };
const EVENT_LABELS = { meeting: 'Meeting', note: 'Note', call: 'Call', team_meeting: 'Team Meeting' };
const CONTACT_BADGES = {
  owner: { label: 'OWNER', cls: 'text-emerald-400 border-emerald-800/40 bg-secondary' },
  expert: { label: 'EXPERT', cls: 'text-violet-400 border-violet-800/40 bg-secondary' },
  internal: { label: 'INTERNAL', cls: 'text-muted-foreground border-border bg-secondary/30' },
  other: { label: 'EXTERNAL', cls: 'text-blue-400 border-blue-800/30 bg-secondary' },
};

function AttioEventCard({ event, isExpanded, onToggle, projectId, onEdited, canEdit, onEditEvent }) {
  const Icon = EVENT_ICONS[event.type] || FileText;
  const date = event.created_at ? new Date(event.created_at) : null;
  const badgeStyle = event.type === 'team_meeting'
    ? 'text-emerald-400 border-emerald-800/40 bg-secondary'
    : event.type === 'meeting'
      ? 'text-blue-400 border-blue-800/30 bg-secondary'
      : 'text-muted-foreground border-border bg-secondary/30';
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editSummary, setEditSummary] = useState('');
  const [saving, setSaving] = useState(false);

  const openEdit = () => {
    setEditTitle(event.plaud_note?.title || '');
    setEditSummary(event.plaud_note?.summary || '');
    setEditing(true);
  };
  const saveEdit = async () => {
    setSaving(true);
    try {
      // Prefer editing on the webex_calls document if the note came from there
      // (webex_call_id is set + no direct event.plaud_note on the event doc), otherwise
      // patch the event's inline plaud_note directly.
      const kind = event.plaud_note_source === 'webex_call' ? 'call' : 'event';
      const targetId = kind === 'call' ? event.webex_call_id : event.id;
      await api.brokerEditPlaudNote({
        kind, id: targetId, project_id: projectId, title: editTitle, summary: editSummary,
      });
      toast.success('Plaud-Notiz aktualisiert');
      setEditing(false);
      onEdited?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    finally { setSaving(false); }
  };

  const delEvent = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`"${event.title || 'Eintrag'}" wirklich löschen?`)) return;
    try {
      await api.deleteEvent(projectId, event.id);
      toast.success('Eintrag gelöscht');
      onEdited?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Löschen fehlgeschlagen'); }
  };

  return (
    <div className="border border-border rounded-sm overflow-hidden" data-testid={`meeting-event-${event.id}`}>
      <button onClick={onToggle} className="w-full flex items-center gap-3 p-3 hover:bg-secondary/20 transition-colors text-left">
        <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 bg-secondary">
          <Icon className={`w-3.5 h-3.5 ${event.type === 'team_meeting' ? 'text-emerald-400' : event.type === 'meeting' ? 'text-blue-400' : 'text-foreground/60'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-foreground">{event.title || 'Untitled'}</span>
            <span className={`text-[9px] font-mono border rounded px-1.5 py-0.5 ${badgeStyle}`}>
              {EVENT_LABELS[event.type] || 'Note'}
            </span>
            {CONTACT_BADGES[event.contact_type] && (
              <span className={`text-[9px] font-mono border rounded px-1.5 py-0.5 ${CONTACT_BADGES[event.contact_type].cls}`}>
                {CONTACT_BADGES[event.contact_type].label}
              </span>
            )}
            {event.is_onsite && (
              <span className="text-[9px] font-mono border rounded px-1.5 py-0.5 text-amber-400 border-amber-800/40 bg-secondary">ON-SITE</span>
            )}
            {event.plaud_note && (
              <span className="text-[9px] font-mono border rounded px-1.5 py-0.5 text-violet-300 border-violet-500/40 bg-violet-500/10 inline-flex items-center gap-1"
                data-testid={`meeting-event-plaud-badge-${event.id}`}>
                <AudioLines className="w-2.5 h-2.5" /> PLAUD
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            {date && <span className="text-[10px] text-muted-foreground font-mono">{date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</span>}
            <span className="text-[10px] text-muted-foreground">by {event.created_by}</span>
            {event.participants?.length > 0 && (
              <span className="text-[10px] text-foreground/80 inline-flex items-center gap-1" data-testid={`meeting-event-participants-${event.id}`}>
                <Users className="w-3 h-3 text-muted-foreground" /> mit {event.participants.join(', ')}
              </span>
            )}
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
      </button>
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-border pt-3">
          {/* If the event was auto-created by Plaud-KI linking, its `notes` is a redundant copy of
              plaud_note.summary — skip it so users don't see the pre-edit version alongside the
              edited one. For any manually-logged meeting where users typed real notes, we still
              render them. */}
          {(event.source === 'plaud_ai' && event.plaud_note) ? null : (event.content_html ? (
            <div className="text-sm text-foreground/80 leading-relaxed rich-note" dangerouslySetInnerHTML={{ __html: event.content_html }} />
          ) : event.content ? (
            <div className="text-sm text-foreground/80 leading-relaxed">
              <NotesMarkdown text={event.content} />
            </div>
          ) : !event.plaud_note && (
            <p className="text-sm text-muted-foreground italic">No content</p>
          ))}
          {event.plaud_note && (
            <div className="mt-3 border border-violet-500/40 bg-violet-500/5 rounded-sm p-3" data-testid={`meeting-event-plaud-${event.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <AudioLines className="w-3.5 h-3.5 text-violet-400" />
                {editing ? (
                  <Input value={editTitle} onChange={e => setEditTitle(e.target.value)}
                    placeholder="Titel"
                    data-testid={`meeting-event-plaud-edit-title-${event.id}`}
                    className="flex-1 h-7 text-xs bg-secondary border-border text-foreground rounded-sm" />
                ) : (
                  <p className="text-xs font-medium text-violet-300 flex-1 truncate">
                    {event.plaud_note.title || 'Plaud-Notiz'}
                  </p>
                )}
                {event.plaud_note.status === 'processing' && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono text-amber-300">
                    <Loader2 className="w-2.5 h-2.5 animate-spin" /> wird zusammengefasst…
                  </span>
                )}
                {!editing && event.plaud_note.linked_by && (
                  <span className="text-[10px] font-mono text-muted-foreground">
                    von {event.plaud_note.linked_by}
                    {event.plaud_note.linked_at ? ` · ${new Date(event.plaud_note.linked_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}` : ''}
                    {event.plaud_note.edited_by && ` · bearbeitet von ${event.plaud_note.edited_by}`}
                  </span>
                )}
                {!editing && event.plaud_note.status !== 'processing' && (
                  <button onClick={(e) => { e.stopPropagation(); openEdit(); }}
                    data-testid={`meeting-event-plaud-edit-${event.id}`}
                    title="Plaud-Notiz bearbeiten"
                    className="text-[10px] text-violet-300 hover:text-violet-200 transition-colors inline-flex items-center gap-1">
                    <Pencil className="w-2.5 h-2.5" /> bearbeiten
                  </button>
                )}
              </div>
              {editing ? (
                <div className="mt-2 space-y-2">
                  <Textarea value={editSummary} onChange={e => setEditSummary(e.target.value)} rows={10}
                    data-testid={`meeting-event-plaud-edit-summary-${event.id}`}
                    className="bg-secondary border-border text-foreground rounded-sm text-xs font-mono" />
                  <div className="flex items-center justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setEditing(false); }}
                      data-testid={`meeting-event-plaud-cancel-${event.id}`}
                      className="text-xs h-7"><XIcon className="w-3 h-3 mr-1" /> Abbrechen</Button>
                    <Button size="sm" onClick={(e) => { e.stopPropagation(); saveEdit(); }} disabled={saving}
                      data-testid={`meeting-event-plaud-save-${event.id}`}
                      className="bg-violet-600 hover:bg-violet-500 text-white rounded-sm text-xs h-7">
                      {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
                      Speichern
                    </Button>
                  </div>
                </div>
              ) : (
                event.plaud_note.summary && (
                  <div className="text-sm text-foreground/80 leading-relaxed mt-2">
                    <NotesMarkdown text={event.plaud_note.summary} />
                  </div>
                )
              )}
            </div>
          )}
          <div className="flex items-center gap-3 mt-3">
            <span className="text-[9px] text-blue-400/50 font-mono flex items-center gap-1">
              <ExternalLink className="w-2.5 h-2.5" /> Logged in OLTA
            </span>
            {canEdit && event.type !== 'team_meeting' && (
              <span className="flex items-center gap-3 ml-auto">
                <button onClick={(e) => { e.stopPropagation(); onEditEvent?.(event); }}
                  data-testid={`meeting-event-edit-${event.id}`}
                  className="text-[10px] text-muted-foreground hover:text-blue-400 transition-colors inline-flex items-center gap-1">
                  <Pencil className="w-2.5 h-2.5" /> bearbeiten
                </button>
                <button onClick={delEvent}
                  data-testid={`meeting-event-delete-${event.id}`}
                  className="text-[10px] text-muted-foreground hover:text-red-400 transition-colors inline-flex items-center gap-1">
                  <Trash2 className="w-2.5 h-2.5" /> löschen
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


export function MeetingNotes({ projectId, isAcMember }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editEvent, setEditEvent] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.getDealMeetings(projectId);
      setEvents(res.data.events || []);
    } catch {
      setEvents([]);
    }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  return (
    <CollapsibleSection
      storageKey={`deal.${projectId}.meetings`}
      icon={Calendar}
      iconClass="text-blue-400"
      title="Meetings & Calls"
      count={!loading ? `${events.length} event${events.length !== 1 ? 's' : ''}` : undefined}
      testId="meetings-calls-section"
      bodyClass="p-4"
      right={!loading && !isAcMember ? (
        <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setAddOpen(true); }} data-testid="add-meeting-btn"
          className="border-blue-600/40 text-blue-400 hover:bg-blue-600/10 rounded-sm text-[11px] h-7 px-2.5">
          <Plus className="w-3 h-3 mr-1" /> Add Call / Meeting
        </Button>
      ) : null}
    >

      {!isAcMember && (
        <AddMeetingDialog projectId={projectId} open={addOpen} onOpenChange={setAddOpen} onSaved={load} />
      )}
      {!isAcMember && editEvent && (
        <AddMeetingDialog projectId={projectId} open={!!editEvent} event={editEvent}
          onOpenChange={(o) => { if (!o) setEditEvent(null); }} onSaved={load} />
      )}

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        </div>
      ) : events.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6">
          No meetings or calls logged yet — use &ldquo;Add Call / Meeting&rdquo; to log every conversation.
        </p>
      ) : (
        <div className="space-y-2">
          {events.map(event => (
            <AttioEventCard
              key={event.id}
              event={event}
              projectId={projectId}
              onEdited={load}
              canEdit={!isAcMember}
              onEditEvent={setEditEvent}
              isExpanded={expandedId === event.id}
              onToggle={() => setExpandedId(expandedId === event.id ? null : event.id)}
            />
          ))}
        </div>
      )}
    </CollapsibleSection>
  );
}
